import {
  Box,
  Typography,
  Skeleton,
  Tooltip,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from '@mui/material';
import SystemPrompt from './SystemPrompt';
import { useAgents } from '../../../hooks/useAgents';
import { useDashboard } from '../../../hooks/useDashboard';
import useCopyToClipboard from '../../../hooks/useCopyToClipboard';
import {
  CopyIcon,
  TreeStructureIcon,
  PencilSimpleIcon,
  CodeIcon,
  GlobeIcon,
  Plus,
} from '@phosphor-icons/react';
import EnhanceSystemPromptDrawer from './EnhanceSystemPrompt/EnhanceSystemPromptDrawer';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConversationFlowsApi,
  updateAssistantConfigValues,
} from '../../../utils/apis';
import CallStagesFlow from '../../../pages/Dashboard/CallStagesFlow';
import DeployUrlsModal from '../../modals/DeployUrlsModal';
import VariablesModal, { VariablePair } from '../../modals/VariablesModal';
import EditAgentDrawer from './EditAgentDrawer';
import MessagePopup from '../../common/MessagePopup';
import { useAlert } from '../../../hooks/useAlert';
import React from 'react';

interface AgentInfoProps {
  isEditable: boolean;
  selectedView: 'single' | 'advance';
  setSelectedView: (view: 'single' | 'advance') => void;
}

const AgentInfo = ({
  isEditable,
  selectedView,
  setSelectedView,
}: AgentInfoProps) => {
  const { isLoading } = useAgents();
  const { localValues, selectedAssistant, isUpdateSystemPromptPending } =
    useDashboard();
  const { copied, copy } = useCopyToClipboard();
  const [enhanceSystemPromptOpen, setEnhanceSystemPromptOpen] = useState(false);
  const [isDeployUrlsModalOpen, setIsDeployUrlsModalOpen] = useState(false);
  const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
  const [isEditSystemPromptDrawerOpen, setIsEditSystemPromptDrawerOpen] =
    useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  // Check if there's a conversation flow for this agent
  const { data: flowsData, isLoading: isLoadingFlows } = useQuery({
    queryKey: ['conversationFlows', selectedAssistant?._id],
    queryFn: () => getConversationFlowsApi(selectedAssistant?._id || ''),
    enabled: !!selectedAssistant?._id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Check if there's an existing flow
  const hasExistingFlow =
    flowsData?.data?.data && flowsData.data.data.length > 0;

  // Set default view based on whether flow exists
  useEffect(() => {
    if (hasExistingFlow) {
      setSelectedView('advance');
    }
  }, [hasExistingFlow, setSelectedView]); // Add setSelectedView to dependencies

  // Updated container styles - remove side padding for flow view
  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    padding:
      selectedView === 'advance' && hasExistingFlow
        ? '0px 0px 24px'
        : '0px 24px 24px',
  };

  // Handle view change
  const handleViewChange = (
    _event: React.MouseEvent<HTMLElement>,
    newView: 'single' | 'advance' | null
  ) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  // Handlers for modals
  const handleDeployUrlsModalClose = () => {
    setIsDeployUrlsModalOpen(!isDeployUrlsModalOpen);
  };

  const handleOpenVariablesModal = () => {
    setIsVariablesModalOpen(true);
  };

  const handleCloseVariablesModal = () => {
    setIsVariablesModalOpen(false);
  };

  const handleOpenEditSystemPromptDrawer = () => {
    setIsEditSystemPromptDrawerOpen(true);
  };

  const handleCloseEditSystemPromptDrawer = () => {
    setIsEditSystemPromptDrawerOpen(false);
  };

  // Variables mutation
  const {
    mutate: updateAssistantConfig,
    isPending: isUpdateAssistantConfigPending,
  } = useMutation({
    mutationFn: updateAssistantConfigValues,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agents'],
      });
      showAlert('Variables saved successfully', 'success');
      handleCloseVariablesModal();
    },
    onError: () => {
      showAlert('Failed to save variables', 'error');
    },
  });

  const handleSaveVariables = (variables: VariablePair[]) => {
    if (!selectedAssistant) return;

    // Get the existing config values
    const configValues = {
      agentId: selectedAssistant._id || '',
      llm: selectedAssistant.config?.llm || '',
      tts: selectedAssistant.config?.tts || '',
      stt: selectedAssistant.config?.stt || '',
      timeZone: selectedAssistant.config?.timeZone || '',
      responseDelay: selectedAssistant.config?.responseDelay,
      voice:
        selectedAssistant.config?.voice ||
        selectedAssistant.config?.voice?.voiceName ||
        '',
      language: selectedAssistant.config?.language || '',
      latency: selectedAssistant.config?.xiLabsConfig?.latency,
      stability: selectedAssistant.config?.xiLabsConfig?.stability,
      speed: selectedAssistant.config?.xiLabsConfig?.speed,
      similarity: selectedAssistant.config?.xiLabsConfig?.similarity,
      variables: variables,
      noiseSuppression: selectedAssistant.config?.noiseSuppression || false,
      userTimeout: selectedAssistant.config?.userTimeout || 0,
      warmTransfer: selectedAssistant.config?.warmTransfer || false,
    };

    // Call the API to update the assistant config
    updateAssistantConfig({
      ...configValues,
      voice: configValues.voice.voiceName || configValues.voice.name,
      language: configValues.language.value,
    });
  };

  const handleCopySystemPrompt = () => {
    const promptFields = [
      { label: 'Identity', value: localValues.identity },
      { label: 'Greetings', value: localValues.firstMessage },
      { label: 'Context', value: localValues.context },
      { label: 'Job', value: localValues.job },
      { label: 'Examples', value: localValues.examples },
      { label: 'Constraints', value: localValues.constraints },
      { label: 'Format', value: localValues.format },
      { label: 'Tone', value: localValues.tone },
    ];

    const formattedPrompt = promptFields
      .filter(field => field.value)
      .map(field => `${field.label}:\n${field.value}`)
      .join('\n\n');

    copy(formattedPrompt);
  };

  const handleConversationFlow = () => {
    navigate(`/call-stage/${selectedAssistant?._id}`);
  };

  // Conditional Edit button handler
  const handleEdit = () => {
    if (selectedView === 'advance' && hasExistingFlow) {
      // If advance view and flow exists, navigate to the route
      navigate(`/call-stage/${selectedAssistant?._id}`);
    } else if (selectedView === 'single') {
      // If single view, open the drawer
      handleOpenEditSystemPromptDrawer();
    }
  };

  // Create a version-like object from current localValues for the EditAgentDrawer
  const currentSystemPromptAsVersion = React.useMemo(() => {
    if (!localValues) return null;

    return {
      // Add any required fields that EditAgentDrawer expects
      version: 'Current',
      ...localValues,
    };
  }, [localValues]);

  if (isLoading || isLoadingFlows) {
    return (
      <Box sx={containerStyles}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="text" width={280} height={20} />
        </Box>

        <Box sx={{ display: 'flex', gap: 4, overflow: 'hidden' }}>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            <SystemPromptSkeleton />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          overflow: 'hidden',
          height: 'calc(100vh - 200px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY:
                selectedView === 'advance' && hasExistingFlow
                  ? 'hidden'
                  : 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            {/* Header with toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mt: 2,
                px: selectedView === 'advance' && hasExistingFlow ? 3 : 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  Prompt:
                </Typography>
                <ToggleButtonGroup
                  value={selectedView}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="prompt type toggle"
                  sx={{
                    backgroundColor: '#DCE0E7',
                    borderRadius: '8px',
                    padding: '4px',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    height: '34px',
                    minWidth: '160px',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: '6px',
                      textTransform: 'none',
                      py: '8px',
                      px: '16px',
                      fontSize: '14px',
                      height: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease-in-out',
                      fontWeight: 400,
                      color: '#4E5A6B',
                      '&.Mui-selected': {
                        backgroundColor: '#FFF',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#2B2F32',
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover': {
                        backgroundColor: '#F5F5F5',
                      },
                    },
                  }}
                >
                  <ToggleButton
                    value="single"
                    sx={{
                      fontFamily: 'DM Sans',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '-1%',
                      textAlign: 'center',
                    }}
                  >
                    Single
                  </ToggleButton>
                  <ToggleButton
                    value="advance"
                    sx={{
                      fontFamily: 'DM Sans',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '-1%',
                      textAlign: 'center',
                    }}
                  >
                    Advance
                  </ToggleButton>
                </ToggleButtonGroup>

                {selectedView === 'single' && (
                  <Tooltip title={copied ? 'Copied!' : 'Copy system prompt'}>
                    <IconButton onClick={handleCopySystemPrompt}>
                      <CopyIcon size={14} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Icon group */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Show Edit button only when appropriate */}
                {(selectedView === 'single' ||
                  (selectedView === 'advance' && hasExistingFlow)) && (
                  <Tooltip title="Edit">
                    <IconButton
                      onClick={handleEdit}
                      disabled={isUpdateSystemPromptPending}
                      sx={{
                        gap: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '6px',
                        '&:hover': {
                          backgroundColor: '#F5F5F5',
                        },
                      }}
                    >
                      <PencilSimpleIcon size={16} />
                      <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                        Edit
                      </Typography>
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Variables">
                  <IconButton
                    onClick={handleOpenVariablesModal}
                    sx={{
                      gap: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: '#F5F5F5',
                      },
                    }}
                  >
                    <CodeIcon size={18} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Variables
                    </Typography>
                  </IconButton>
                </Tooltip>

                <Tooltip title="Deploy URLs">
                  <IconButton
                    onClick={handleDeployUrlsModalClose}
                    sx={{
                      gap: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '6px',
                    }}
                  >
                    <GlobeIcon size={18} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Deploy URLs
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Content rendering based on selected view */}
            {selectedView === 'single' ? (
              <SystemPrompt isEditable={isEditable} />
            ) : (
              // Advance view
              <>
                {hasExistingFlow ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      mt: 2,
                    }}
                  >
                    <CallStagesFlow
                      showSidebar={false}
                      agentId={selectedAssistant?._id}
                      readOnly={true}
                    />
                  </Box>
                ) : (
                  // Show create flow button when no flow exists
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '60vh',
                      gap: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <TreeStructureIcon size={48} color="#9CA3AF" />
                      <Typography
                        sx={{
                          fontSize: '18px',
                          fontWeight: 500,
                          color: '#374151',
                          textAlign: 'center',
                        }}
                      >
                        No Conversation Flow Created
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#6B7280',
                          textAlign: 'center',
                          maxWidth: '400px',
                        }}
                      >
                        Create a conversation flow to define the stages and
                        structure of your assistant's conversations.
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={handleConversationFlow}
                      startIcon={<Plus size={14} />}
                      sx={{
                        px: 3,
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '8px',
                      }}
                    >
                      Create Conversation Flow
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Modals */}
      <DeployUrlsModal
        open={isDeployUrlsModalOpen}
        onClose={handleDeployUrlsModalClose}
      />

      <VariablesModal
        open={isVariablesModalOpen}
        onClose={handleCloseVariablesModal}
        onSave={handleSaveVariables}
        isLoading={isUpdateAssistantConfigPending}
        initialVariables={[{ key: '', value: '' }]}
      />

      <EditAgentDrawer
        open={isEditSystemPromptDrawerOpen}
        onClose={handleCloseEditSystemPromptDrawer}
        version={currentSystemPromptAsVersion}
      />

      <MessagePopup alert={alert} setAlert={setAlert} />

      <EnhanceSystemPromptDrawer
        open={enhanceSystemPromptOpen}
        onClose={() => setEnhanceSystemPromptOpen(false)}
      />
    </Box>
  );
};

export default AgentInfo;

// Skeleton for System Prompt
const SystemPromptSkeleton = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {[...Array(8)].map((_, index) => (
        <Box key={index} sx={{ mb: 2 }}>
          <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={56}
            sx={{ borderRadius: '6px' }}
          />
        </Box>
      ))}
    </Box>
  );
};
